import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fileUrl, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/layout/Layout";
import { Loading, ErrorBox, Empty } from "@/components/common/States";
import { ListingCard } from "@/components/common/Cards";
import { Stars, VerifiedTick } from "@/components/common/Badges";
import ReportButton from "@/components/common/ReportButton";
import SEO from "@/components/common/SEO";

const CRIT = [["communication", "İletişim"], ["accuracy", "İlan bilgilerinin doğruluğu"], ["experience", "Alışveriş deneyimi"]];

function SellerReviews({ sellerId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [f, setF] = useState({ communication: 0, accuracy: 0, experience: 0, comment: "" });
  const { data } = useQuery({ queryKey: ["seller-reviews", sellerId], queryFn: async () => (await api.get(`/sellers/${sellerId}/reviews`)).data });
  const submit = async () => {
    try { await api.post(`/sellers/${sellerId}/reviews`, f); toast.success("Değerlendirme eklendi"); qc.invalidateQueries({ queryKey: ["seller-reviews", sellerId] }); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <Loading />;
  return (
    <section className="space-y-4" data-testid="seller-reviews">
      <h2 className="font-display text-xl font-semibold">Satıcı değerlendirmeleri <span className="text-xs font-normal text-zinc-500">(ürün puanından bağımsız)</span></h2>
      <div className="flex items-center gap-3" data-testid="seller-rating-summary"><span className="font-display text-3xl font-semibold">{data.summary.average ?? "–"}</span><span className="text-xs text-zinc-500">{data.summary.count} değerlendirme</span></div>
      <p className="text-xs text-yellow-700">Alışverişler site dışında gerçekleştiği için değerlendirmeler doğrulanmış alışverişe dayanmaz.</p>
      {user && user.id !== sellerId && (
        <div className="panel space-y-2 p-4">
          {CRIT.map(([k, l]) => <div key={k} className="flex items-center justify-between text-sm"><span>{l}</span><Stars value={f[k]} onChange={(n) => setF({ ...f, [k]: n })} testId={`seller-review-${k}`} /></div>)}
          <Textarea data-testid="seller-review-comment" placeholder="Yorumunuz" value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} />
          <Button onClick={submit} data-testid="seller-review-submit" className="bg-[#0071e3] text-white">Gönder</Button>
        </div>
      )}
      {data.items.map((r) => (
        <div key={r.id} className="panel p-4 text-sm"><div className="flex justify-between"><Stars value={r.rating} size={3} /><span className="text-xs text-zinc-500">{r.user_name} · {formatDate(r.created_at)}</span></div>
          <p className="mt-2 text-zinc-700">{r.comment}</p><ReportButton targetType="seller_review" targetId={r.id} /></div>
      ))}
    </section>
  );
}

export default function Seller() {
  const { id } = useParams();
  const { data, isLoading, error } = useQuery({ queryKey: ["seller", id], queryFn: async () => (await api.get(`/sellers/${id}`)).data });
  if (isLoading) return <Loading />;
  if (error) return <Page><ErrorBox message={errMsg(error)} /></Page>;
  return (
    <Page>
      <div className="flex flex-wrap items-center gap-4" data-testid="seller-profile">
        {data.avatar_url ? <img src={fileUrl(data.avatar_url)} alt={data.full_name || "Satıcı profili"} loading="lazy" decoding="async" className="h-20 w-20 rounded-full object-cover" /> : <div className="grid h-20 w-20 place-items-center rounded-full bg-black/[0.05] text-2xl">{data.full_name?.[0]}</div>}
        <div>
          <h1 className="font-display text-3xl font-semibold" data-testid="seller-name">{data.full_name} <VerifiedTick show={data.is_verified_seller} /></h1>
          <p className="text-sm text-zinc-500">{data.city && `${data.city} · `}Üyelik: {formatDate(data.created_at)}</p>
          {data.bio && <p className="mt-1 max-w-xl text-sm text-zinc-700">{data.bio}</p>}
        </div>
        <div className="ml-auto"><ReportButton targetType="user" targetId={data.id} label="Kullanıcıyı bildir" /></div>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <section>
          <h2 className="mb-4 font-display text-xl font-semibold">Aktif ilanlar ({data.listings.length})</h2>
          {data.listings.length ? <div className="grid gap-4 sm:grid-cols-2">{data.listings.map((l) => <ListingCard key={l.id} item={l} />)}</div> : <Empty title="Aktif ilan yok" />}
        </section>
        {data.seller_reviews_enabled ? <SellerReviews sellerId={data.id} /> : (
          <section className="panel h-fit p-5 text-sm text-zinc-500" data-testid="seller-reviews-disabled">
            <p className="font-display font-semibold text-zinc-900">Satıcı değerlendirmeleri</p>
            <p className="mt-1">Satıcı puanlama sistemi hazır ancak henüz aktif değil.</p>
          </section>
        )}
      </div>
    </Page>
  );
}
